import React, { ReactNode, useState } from 'react';
import Permissions from '../auth/Permissions';
import { FormattedMessage } from 'react-intl';
import {
  IconBuilding,
  IconCalendarEvent,
  IconEye,
  IconFolderUpload,
  IconHelp,
  IconListDetails,
  IconRefresh,
  IconTag,
} from '../../icons';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from '../core/Menu';
import ImportRebusDataDialog from '../ImportRebusDataDialog/ImportRebusDataDialog';
import useSchemaManagementDialogStore from '../../store/schemaManagementDialog';
import useCheckInManagementDialogStore from '../../store/checkInManagementDialog';
import useRecurringWorkOrderPlanManagementDialogStore from '../../store/recurringWOPlanManagementDialog';
import useWOInstructionManagementDialog from '../../store/woInstructionManagementDialog';
import useGuideStore from '../../store/guide';
import useTagManagementDialog from '../../store/tagManagementDialog';
import useContractorManagementDialogStore from '../../store/contractorManagementDialog';
import FeatureLicense from '../auth/FeatureLicense';

interface MainMenuProps {
  /** The element that opens the menu. Radix needs it inside the menu's tree. */
  trigger: ReactNode;
}

const MainMenu: React.FC<MainMenuProps> = ({ trigger }) => {
  const { open: openTagManagementDialog } = useTagManagementDialog();
  const { setShow: setContractorManagementDialogOpen } =
    useContractorManagementDialogStore();
  const { open: openCheckInManagementDialog } =
    useCheckInManagementDialogStore();
  const { open: openWOInstructionManagementDialog } =
    useWOInstructionManagementDialog();
  const { open: openRecurringWorkOrderPlanManagementDialog } =
    useRecurringWorkOrderPlanManagementDialogStore();
  const [importRebusDataOpen, setImportRebusDataOpen] = useState(false);
  const { setShow: setSchemaManagementDialogOpen } =
    useSchemaManagementDialogStore();

  const { openGuide } = useGuideStore();

  return (
    <>
      <ImportRebusDataDialog
        open={importRebusDataOpen}
        onClose={() => setImportRebusDataOpen(false)}
      />
      {/* modal={false} avoids the Radix bug where opening a modal Dialog from a
          menu item leaves `pointer-events: none` stuck on <body>, which would
          make the whole app (incl. the topbar) non-interactive after closing
          that dialog. */}
      <Menu modal={false}>
        <MenuTrigger asChild>{trigger}</MenuTrigger>
        <MenuContent align="start" className="py-1">
          <Permissions has="entities:create">
            <MenuItem onSelect={() => setSchemaManagementDialogOpen(true)}>
              <IconListDetails className="size-6" />
              <FormattedMessage
                id="main_menu.manage_schema"
                defaultMessage="Schema"
              />
            </MenuItem>
          </Permissions>
          <Permissions oneOf={['recurring-workorder-plans:create']}>
            <MenuItem
              onSelect={() => openRecurringWorkOrderPlanManagementDialog()}
            >
              <IconRefresh className="size-6" />
              <FormattedMessage
                id="main_menu.manage_recurring_plans"
                defaultMessage="Recurring work order plans"
              />
            </MenuItem>
          </Permissions>
          <Permissions oneOf={['checkin-plan:create']}>
            <MenuItem onSelect={() => openCheckInManagementDialog()}>
              <IconEye className="size-6" />
              <FormattedMessage
                id="main_menu.manage_check_in_plans"
                defaultMessage="Check-in plans"
              />
            </MenuItem>
          </Permissions>
          <Permissions has="tags:update">
            <MenuItem onSelect={() => openTagManagementDialog()}>
              <IconTag className="size-6" />
              <FormattedMessage
                id="main_menu.manage_tags"
                defaultMessage="Tags"
              />
            </MenuItem>
          </Permissions>
          <Permissions oneOf={['role:admin', 'role:contractor-admin']}>
            <MenuItem onSelect={() => setContractorManagementDialogOpen(true)}>
              <IconBuilding className="size-6" />
              <FormattedMessage
                id="main_menu.manage_contractors"
                defaultMessage="Contractors"
              />
            </MenuItem>
          </Permissions>
          <Permissions has="instructions:create">
            <MenuItem onSelect={() => openWOInstructionManagementDialog()}>
              <IconCalendarEvent className="size-6" />
              <FormattedMessage
                id="main_menu.manage_wo_templates"
                defaultMessage="Work order templates"
              />
            </MenuItem>
          </Permissions>
          <Permissions oneOf={['role:admin']}>
            <FeatureLicense has="rebus_import">
              <MenuSeparator />
              <MenuItem onSelect={() => setImportRebusDataOpen(true)}>
                <IconFolderUpload className="size-6" />
                <FormattedMessage
                  id="main_menu.import_rebus_data"
                  defaultMessage="Import rebus data"
                />
              </MenuItem>
            </FeatureLicense>
          </Permissions>
          <MenuSeparator />
          <MenuItem onSelect={() => openGuide()}>
            <IconHelp className="size-6" />
            <FormattedMessage id="main_menu.guide" defaultMessage="Help" />
          </MenuItem>
        </MenuContent>
      </Menu>
    </>
  );
};

export default MainMenu;
